import {
  Stack,
  Title,
  Group,
  Button,
  Text,
  Card,
  ActionIcon,
  Tooltip,
  TextInput,
  Loader,
  Badge,
} from '@mantine/core';
import { useParams } from 'react-router-dom';
import { FC, useState } from 'react';
import { Practitioner } from '@medplum/fhirtypes';
import {
  IconTrash,
  IconUser,
  IconSearch,
  IconPlus,
  IconBuilding,
} from '@tabler/icons-react';
import { usePatient } from '@hooks/usePatient';
import { useAssignedDoctors, AssignedDoctor } from '@hooks/useAssignedDoctors';
import { formatHumanName } from '@medplum/core';
import { useDoctorSearch } from '@hooks/useDoctorSearch';
import { useExternalDoctors } from '@hooks/useExternalDoctors';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { ARKON } from '@consts/systems.consts';
import { useTranslation } from 'react-i18next';
import { Panel } from '@medplum/react';

//------------------------------------------------------------------------------
export const AssignedDoctorsTab: FC = () => {
  const { t } = useTranslation('practitioners');
  const { patientId } = useParams();
  const patient = usePatient();
  const { assignedDoctors, addDoctor, removeDoctor } =
    useAssignedDoctors(patient);
  // Search states
  const [internalSearchQuery, setInternalSearchQuery] = useState(''); // Search hooks
  const {
    setSearchQuery: setAllDoctorsSearchQuery,
    practitioners: allPractitioners,
    loading: allDoctorsLoading,
  } = useDoctorSearch();
  const {
    searchQuery: externalSearchQueryState,
    debouncedSearch: debouncedExternalSearch,
    setSearchQuery: setExternalSearchQuery,
    externalDoctors,
    getOrganizationName,
    loading: externalDoctorsLoading,
  } = useExternalDoctors();

  //----------------------------------------------------------------------------
  const handleAddInternalDoctor = async (practitioner: Practitioner) => {
    await addDoctor(practitioner, 'assigned');
    setInternalSearchQuery('');
    setAllDoctorsSearchQuery('');
  };
  const handleAddExternalDoctor = async (practitioner: Practitioner) => {
    await addDoctor(practitioner, 'referral');
    setExternalSearchQuery('');
  };

  if (!patientId) {
    return null;
  }

  // Separate doctors into internal and external lists
  const internalDoctors = assignedDoctors.filter(
    (doctor) => doctor.role !== 'Referral Doctor'
  );
  const externalAssignedDoctors = assignedDoctors.filter(
    (doctor) => doctor.role === 'Referral Doctor'
  );
  // Filter practitioners for internal search (exclude external doctors and already assigned doctors)
  const internalSearchResults =
    internalSearchQuery.length >= 2
      ? allPractitioners.filter(
          (practitioner) =>
            !practitioner.extension?.some(
              (ext) => ext.url === ARKON.SD.EXTERNAL_ORGANIZATION_EXTENSION
            ) &&
            !assignedDoctors.some(
              (assignedDoctor) => assignedDoctor.id === practitioner.id
            )
        )
      : [];
  // Filter external doctors for external search (exclude already assigned external doctors)
  const externalSearchResults =
    debouncedExternalSearch.length >= 2
      ? externalDoctors.filter(
          (practitioner) =>
            !externalAssignedDoctors.some(
              (assignedDoctor) => assignedDoctor.id === practitioner.id
            )
        )
      : [];

  // Update search queries when typing
  const handleInternalSearchChange = (value: string) => {
    setInternalSearchQuery(value);
    setAllDoctorsSearchQuery(value);
  };
  const renderDoctorCard = (doctor: AssignedDoctor) => (
    <Card key={doctor.id} withBorder padding="md">
      <Group justify="space-between" align="flex-start">
        <Group align="flex-start" gap="md">
          <IconUser size={24} color="gray" />
          <Stack gap={4}>
            <Text fw={600} size="lg">
              {formatHumanName(doctor.practitioner.name?.[0])}
            </Text>
            <Text size="sm" c="dimmed">
              {doctor.role || t('assignedDoctors.labels.practitioner')}
            </Text>

            {doctor.practitioner.telecom && (
              <Stack gap={2}>
                {doctor.practitioner.telecom
                  .filter((telecom) => telecom.system === 'phone')
                  .map((phone, index) => (
                    <Group key={index} gap="xs" align="center">
                      <Text size="sm" fw={500}>
                        {t('assignedDoctors.labels.phone')}
                      </Text>
                      <Text size="sm">{phone.value || ''}</Text>
                    </Group>
                  ))}

                {doctor.practitioner.telecom
                  .filter((telecom) => telecom.system === 'email')
                  .map((email, index) => (
                    <Group key={index} gap="xs" align="center">
                      <Text size="sm" fw={500}>
                        {t('assignedDoctors.labels.email')}
                      </Text>
                      <Text size="sm">{email.value}</Text>
                    </Group>
                  ))}
              </Stack>
            )}
          </Stack>
        </Group>
        <Tooltip label={t('assignedDoctors.buttons.remove')}>
          <ActionIcon
            color="red"
            variant="subtle"
            onClick={() => removeDoctor(doctor.id)}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Card>
  );

  const renderSearchResult = (
    practitioner: Practitioner,
    isExternal: boolean,
    onAdd: () => void
  ) => (
    <Card key={practitioner.id} withBorder padding="sm" bg="gray.0">
      <Group justify="space-between" align="flex-start">
        <Group align="flex-start" gap="md" style={{ flex: 1 }}>
          <IconUser size={20} color="gray" />
          <Stack gap={2} style={{ flex: 1 }}>
            <Group gap="xs" align="center">
              <Text fw={600} size="sm">
                {formatHumanName(practitioner.name?.[0])}
              </Text>
              {isExternal && (
                <Badge
                  size="xs"
                  color="orange"
                  variant="light"
                  leftSection={<IconBuilding size={10} />}
                >
                  {getOrganizationName(practitioner)}
                </Badge>
              )}
            </Group>

            {practitioner.qualification &&
              practitioner.qualification.length > 0 && (
                <Group gap="xs">
                  {practitioner.qualification.slice(0, 2).map((qual, index) => (
                    <Badge key={index} size="xs" variant="light">
                      {qual.code?.text ||
                        qual.code?.coding?.[0]?.display ||
                        t('assignedDoctors.labels.qualification')}
                    </Badge>
                  ))}
                </Group>
              )}

            {practitioner.telecom && (
              <Stack gap={1}>
                {practitioner.telecom
                  .filter((telecom) => telecom.system === 'phone')
                  .slice(0, 1)
                  .map((phone, index) => (
                    <Text key={index} size="xs" c="dimmed">
                      {t('assignedDoctors.labels.phone')}{' '}
                      {formatPhoneNumberIntl(phone.value || '')}
                    </Text>
                  ))}
                {practitioner.telecom
                  .filter((telecom) => telecom.system === 'email')
                  .slice(0, 1)
                  .map((email, index) => (
                    <Text key={index} size="xs" c="dimmed">
                      {t('assignedDoctors.labels.email')} {email.value}
                    </Text>
                  ))}
              </Stack>
            )}
          </Stack>
        </Group>

        <Button
          size="xs"
          variant="light"
          leftSection={<IconPlus size={12} />}
          onClick={onAdd}
        >
          {isExternal
            ? t('assignedDoctors.buttons.addExternal')
            : t('assignedDoctors.buttons.assignInternal')}
        </Button>
      </Group>
    </Card>
  );
  //----------------------------------------------------------------------------
  return (
    <Panel mx="lg">
      <Stack gap="md">
        <Title order={3}>{t('assignedDoctors.pageTitle')}</Title>

        {/* Internal Doctors Section */}
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Title order={4} c="blue">
              {t('assignedDoctors.internalTitle')}
            </Title>
          </Group>

          {/* Internal Doctor Search */}
          <TextInput
            placeholder={t('assignedDoctors.searchInternalPlaceholder')}
            leftSection={<IconSearch size={16} />}
            value={internalSearchQuery}
            onChange={(event) =>
              handleInternalSearchChange(event.currentTarget.value)
            }
            rightSection={allDoctorsLoading ? <Loader size="xs" /> : null}
          />

          {/* Internal Search Results */}
          {internalSearchQuery.length >= 2 &&
            internalSearchResults.length > 0 && (
              <Stack gap="xs" mb="md">
                <Text size="sm" fw={500} c="dimmed">
                  {t('assignedDoctors.searchResults')}
                </Text>
                {internalSearchResults.map((practitioner) =>
                  renderSearchResult(practitioner, false, () =>
                    handleAddInternalDoctor(practitioner)
                  )
                )}
              </Stack>
            )}

          {internalSearchQuery.length >= 2 &&
            internalSearchResults.length === 0 &&
            !allDoctorsLoading && (
              <Text c="dimmed" ta="center" py="sm" size="sm">
                {t('assignedDoctors.noInternalFound', {
                  query: internalSearchQuery,
                })}
              </Text>
            )}

          {/* Internal Doctors List */}
          {internalDoctors.length === 0 ? (
            <Text c="dimmed" ta="center" py="md">
              {t('assignedDoctors.noInternalAssigned')}
            </Text>
          ) : (
            <Stack gap="sm">{internalDoctors.map(renderDoctorCard)}</Stack>
          )}
        </Stack>

        {/* External Doctors (Referrals) Section */}
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Title order={4} c="orange">
              {t('assignedDoctors.externalTitle')}
            </Title>
          </Group>{' '}
          {/* External Doctor Search */}
          <TextInput
            placeholder={t('assignedDoctors.searchExternalPlaceholder')}
            leftSection={<IconSearch size={16} />}
            value={externalSearchQueryState}
            onChange={(event) =>
              setExternalSearchQuery(event.currentTarget.value)
            }
            rightSection={externalDoctorsLoading ? <Loader size="xs" /> : null}
          />
          {/* External Search Results */}
          {debouncedExternalSearch.length >= 2 &&
            externalSearchResults.length > 0 && (
              <Stack gap="xs" mb="md">
                <Text size="sm" fw={500} c="dimmed">
                  {t('assignedDoctors.searchResults')}
                </Text>
                {externalSearchResults.map((practitioner) =>
                  renderSearchResult(practitioner, true, () =>
                    handleAddExternalDoctor(practitioner)
                  )
                )}
              </Stack>
            )}

          {debouncedExternalSearch.length >= 2 &&
            externalSearchResults.length === 0 &&
            !externalDoctorsLoading && (
              <Text c="dimmed" ta="center" py="sm" size="sm">
                {t('assignedDoctors.noExternalFound', {
                  query: debouncedExternalSearch,
                })}
              </Text>
            )}
          {/* External Doctors List */}
          {externalAssignedDoctors.length === 0 ? (
            <Text c="dimmed" ta="center" py="md">
              {t('assignedDoctors.noExternalAssigned')}
            </Text>
          ) : (
            <Stack gap="sm">
              {externalAssignedDoctors.map(renderDoctorCard)}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Panel>
  );
};
